// noble-ciphers exports AES-GCM-SIV as `siv` (the only SIV-mode AEAD it ships)
// from the `aes` entrypoint. Don't confuse with RFC 5297 AES-SIV — different algorithm.
import { siv as aesGcmSiv } from "@noble/ciphers/aes";
import { gzipSync, gunzipSync } from "fflate";
import {
  TINK_AEAD_PREFIX_LEN,
  getPrimaryKey,
  parseTinkKeyset,
  readTinkAeadPrefix,
  tinkAeadPrefix,
  type TinkKey,
  type TinkKeyset,
} from "./tink-keyset";

/**
 * The TS-side mirror of the Android client's `PayloadEncryption` +
 * `OctiServerConnector.{readModule, writeModule}` wrap, with one
 * end-to-end shape:
 *
 *   write: plaintext  -gzip→  inner  -AEAD(key, nonce, AD)→  ct+tag
 *          → wire: 0x01 || keyId(4 BE) || nonce(12) || ct+tag
 *
 *   read:  inverse — strip prefix, verify keyId, extract nonce, AEAD-decrypt, gunzip.
 *
 * The AD must equal UTF-8 of `"${targetDeviceId}:${moduleId}"` (matches
 * `OctiServerConnector.buildAssociatedData` in app-main).
 *
 * Wire-format compatibility with the JVM client is pinned by `payload.test.ts`
 * via fixtures generated by `TinkVectorsExportTest`.
 */

const NONCE_LEN = 12;

export interface PayloadEncryption {
  /** Encrypts `plaintext` with the supplied AD; returns wire-format ciphertext bytes. */
  encrypt(plaintext: Uint8Array, ad: Uint8Array): Uint8Array;
  /** Decrypts wire-format ciphertext bytes; throws on auth/AD/key-id mismatch. */
  decrypt(ciphertext: Uint8Array, ad: Uint8Array): Uint8Array;
}

/**
 * Construct a {@link PayloadEncryption} bound to a specific Tink keyset.
 *
 * Pass the same keyset bytes the Android client persists in
 * `OctiServer.Credentials.encryptionKeyset.key` (the raw output of
 * `TinkProtoKeysetFormat.serializeKeyset`).
 */
export function createPayloadEncryption(keysetBytes: Uint8Array): PayloadEncryption {
  const keyset = parseTinkKeyset(keysetBytes);
  const primary = getPrimaryKey(keyset);
  return {
    encrypt: (plaintext, ad) => encryptInternal(primary, plaintext, ad),
    decrypt: (ciphertext, ad) => decryptInternal(primary, ciphertext, ad),
  };
}

/**
 * Convenience: build the AD bytes for a `(deviceId, moduleId)` pair, mirroring
 * `OctiServerConnector.buildAssociatedData` in the Android client.
 */
export function buildAssociatedData(deviceId: string, moduleId: string): Uint8Array {
  return new TextEncoder().encode(`${deviceId}:${moduleId}`);
}

/** Re-export for callers that need the parsed keyset directly. */
export { parseTinkKeyset, type TinkKeyset, type TinkKey };

function encryptInternal(primary: TinkKey, plaintext: Uint8Array, ad: Uint8Array): Uint8Array {
  // Outer gzip wrap matches OctiServerConnector.kt:586 (`module.payload.toGzip()`).
  // gzipSync with no options uses level 6, same default fflate ships with — small files
  // (e.g. clipboard text) may grow ~20 bytes due to the gzip header, which is fine.
  const compressed = gzipSync(plaintext);
  const nonce = randomBytes(NONCE_LEN);
  const ct = aesGcmSiv(primary.keyBytes, nonce, ad).encrypt(compressed);
  // Wire layout: prefix(5) || nonce(12) || ct+tag(N+16)
  const out = new Uint8Array(TINK_AEAD_PREFIX_LEN + NONCE_LEN + ct.length);
  out.set(tinkAeadPrefix(primary.keyId), 0);
  out.set(nonce, TINK_AEAD_PREFIX_LEN);
  out.set(ct, TINK_AEAD_PREFIX_LEN + NONCE_LEN);
  return out;
}

function decryptInternal(primary: TinkKey, wire: Uint8Array, ad: Uint8Array): Uint8Array {
  readTinkAeadPrefix(wire, primary.keyId);
  if (wire.length < TINK_AEAD_PREFIX_LEN + NONCE_LEN + 16) {
    // Need at least 12-byte nonce + 16-byte GCM-SIV tag after the prefix
    throw new Error("payload: ciphertext too short to contain nonce + tag");
  }
  const nonce = wire.subarray(TINK_AEAD_PREFIX_LEN, TINK_AEAD_PREFIX_LEN + NONCE_LEN);
  const ct = wire.subarray(TINK_AEAD_PREFIX_LEN + NONCE_LEN);
  const compressed = aesGcmSiv(primary.keyBytes, nonce, ad).decrypt(ct);
  return gunzipSync(compressed);
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}
